import {
  DermloopSksCodeType,
  DiagnosticConfidence,
} from '@dermloop/static-values';
import { Exclude, Transform } from 'class-transformer';
import {
  IsArray,
  ArrayNotEmpty,
  IsAlphanumeric,
  IsBoolean,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';
import { BaseCreateDto, BaseUpdateDto } from '../base.dto';
import { NamedEntityDto } from '../clinical-details';
import { ConditionTypeDto } from '../condition-type.dtos';

export type DiagnosisInfo = {
  diagnosis?: string;
  confidence?: DiagnosticConfidence;
  diagnosisGroups?: string[];
};

export interface Icd10Dto {
  code: string;
  description: string;
}

export interface SksDto {
  code: string;
  name: string;
  codeType: DermloopSksCodeType;
}

export interface DiagnosisDto extends NamedEntityDto {
  requiresUserInput?: boolean;
  shortName?: string;
  icd10Code?: string;
  sksDiagnosisCode?: string;
  sksAdditionalCode?: string;
  legacyIdentifier?: string;
  description?: string;
  snomed?: string;
  visible?: boolean;
  malignant?: boolean;
  severity?: number;
  conditionTypes?: ConditionTypeDto[];
  featuredForConditionTypes?: ConditionTypeDto[];
  /** @deprecated Use conditionTypes. Kept for pre-rename clients that still read this field. */
  skinTypeConditions?: ConditionTypeDto[];
  /** @deprecated Use featuredForConditionTypes. Kept for pre-rename clients that still read this field. */
  featuredForSkinTypeConditions?: ConditionTypeDto[];
  parentDiagnosis?: DiagnosisDto;
  parentDiagnosisId?: string;
  simplifiedDiagnosisDescription?: NamedEntityDto;
  undiagnosable?: boolean;
}

export class ListDiagnosesQueryDto {
  /** Omit for the full catalogue; an empty selection is rejected. */
  @IsOptional()
  @IsArray()
  @ArrayNotEmpty()
  @IsUUID('all', { each: true })
  @Transform(({ value }) => (typeof value === 'string' ? [value] : value))
  conditionTypeIds?: string[];
}

/** `mostUsed` ranks from the newest this many answered cases. */
export const MOST_USED_DIAGNOSES_WINDOW = 500;

export enum DiagnosisSearchOrder {
  /** The caller's own primary diagnoses, most used first. */
  MostUsed = 'mostUsed',
}

export class SearchDiagnosesDto {
  @IsUUID()
  conditionTypeId: string;

  @IsEnum(DiagnosisSearchOrder)
  orderBy: DiagnosisSearchOrder;

  @IsOptional()
  @IsInt()
  @Min(1)
  limit?: number;

  /** With fewer answered cases of the condition type, `mostUsed` returns nothing. */
  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MOST_USED_DIAGNOSES_WINDOW)
  minAnsweredCases?: number;
}

export class CreateDiagnosisDto
  extends BaseCreateDto
  implements Partial<DiagnosisDto>
{
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsString()
  name_da?: string;

  @IsOptional()
  @IsString()
  name_de?: string;

  @IsOptional()
  @IsString()
  shortName?: string;

  @IsOptional()
  @IsString()
  @MinLength(3)
  @MaxLength(20)
  @IsAlphanumeric()
  icd10Code?: string;

  @IsOptional()
  @IsString()
  @MinLength(2)
  @MaxLength(20)
  @IsAlphanumeric()
  sksDiagnosisCode?: string;

  @IsOptional()
  @IsString()
  @MinLength(2)
  @MaxLength(20)
  @IsAlphanumeric()
  sksAdditionalCode?: string;

  @IsOptional()
  @IsString()
  legacyIdentifier?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsBoolean()
  visible: boolean;

  @IsBoolean()
  malignant: boolean;

  @IsBoolean()
  undiagnosable: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  severity?: number;

  @IsUUID('all', { each: true })
  @ArrayNotEmpty()
  conditionTypeIds: string[];

  @Exclude()
  conditionTypes?: never;

  @IsOptional()
  @IsUUID('all', { each: true })
  featuredForConditionTypeIds?: string[];

  @Exclude()
  featuredForConditionTypes?: never;

  @IsOptional()
  @IsUUID()
  parentDiagnosisId?: string;

  @IsOptional()
  @IsUUID()
  simplifiedDiagnosisDescriptionId?: string;
}

export class UpdateDiagnosisDto
  extends BaseUpdateDto
  implements Partial<DiagnosisDto>
{
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  name?: string;

  @IsOptional()
  @IsString()
  name_da?: string;

  @IsOptional()
  @IsString()
  name_de?: string;

  @IsOptional()
  @IsString()
  shortName?: string;

  @IsOptional()
  @IsString()
  @MinLength(3)
  @MaxLength(20)
  @IsAlphanumeric()
  icd10Code?: string;

  @IsOptional()
  @IsString()
  @MinLength(2)
  @MaxLength(20)
  @IsAlphanumeric()
  sksDiagnosisCode?: string;

  @IsOptional()
  @IsString()
  @MinLength(2)
  @MaxLength(20)
  @IsAlphanumeric()
  sksAdditionalCode?: string;

  @IsOptional()
  @IsString()
  legacyIdentifier?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsBoolean()
  visible?: boolean;

  @IsOptional()
  @IsBoolean()
  malignant?: boolean;

  @IsOptional()
  @IsBoolean()
  undiagnosable?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  severity?: number;

  @IsOptional()
  @IsUUID('all', { each: true })
  @ArrayNotEmpty()
  conditionTypeIds?: string[];

  @Exclude()
  conditionTypes?: never;

  @IsOptional()
  @IsUUID('all', { each: true })
  featuredForConditionTypeIds?: string[];

  @Exclude()
  featuredForConditionTypes?: never;

  @IsOptional()
  @IsUUID()
  parentDiagnosisId?: string;

  @IsOptional()
  @IsUUID()
  simplifiedDiagnosisDescriptionId?: string;
}

export class SearchIcd10Dto {
  @IsNotEmpty()
  @IsString()
  @MaxLength(20)
  @IsAlphanumeric()
  icd10Code: string;
}

export class SearchSksDto {
  @IsNotEmpty()
  @IsString()
  @MaxLength(20)
  @IsAlphanumeric()
  sksCode: string;

  @IsNotEmpty()
  @IsEnum(DermloopSksCodeType)
  codeType: DermloopSksCodeType;
}
